import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';

import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import LessonCountShowcase from '../components/LessonCountShowcase';

import {
  Rocket,
  BookOpen,
  Brain,
  ChartLineUp,
  Users,
  CheckCircle,
  ArrowRight,
  Calculator,
  Atom,
  Globe,
  Scroll,
  Translate,
  GraduationCap,
  Lock,
  Fire,
  Lightbulb,
  Target,
  Sparkle,
  Star,
} from '@phosphor-icons/react';

import { trackEvent, EVT } from '../utils/analytics';

function getSchoolYearStage() {
  const now = new Date();
  const month = now.getMonth();

  if (month >= 8 && month <= 10) {
    return {
      badge: 'Back to school • GCSE 2027 prep',
      title: 'Back to school, back to momentum.',
      highlight: 'Build the habits that make GCSE 2027 feel manageable.',
      description:
        'The new school year is here. Help Year 10 and Year 11 students lock in strong revision routines, cover core topics early and build confidence before the 2027 GCSE stretch begins.',
      seoDescription:
        'New school year GCSE 2027 prep for Year 10 and Year 11. Structured maths, English, science and geography revision with AI feedback and progress tracking.',
      proof: [
        'Autumn term routine built in',
        'GCSE 2027 foundations first',
        'Track weak areas early',
        'Parents stay in the loop',
      ],
    };
  }

  if (month >= 11 || month <= 1) {
    return {
      badge: 'Winter term • strengthen the basics',
      title: 'Winter term is for tightening the gaps.',
      highlight: 'Turn revision into a consistent GCSE 2027 system.',
      description:
        'As the first term settles, students need steady routines and clear feedback. Use structured topic practice to fix weak areas before GCSE pressure ramps up in the spring.',
      seoDescription:
        'Winter term GCSE revision plan for Year 10 and Year 11. Build strong foundations, fix weak areas and prepare for 2027 exams with structured practice and AI feedback.',
      proof: [
        'Fix weak areas fast',
        'Strong routines before mocks',
        'Parent-led progress check-ins',
        'Built for Year 10 and 11',
      ],
    };
  }

  if (month >= 2 && month <= 4) {
    return {
      badge: 'Spring term • mock season is coming',
      title: 'Mock season is here — now is the time to sharpen up.',
      highlight: 'That strong revision habit becomes a Grade 9 plan.',
      description:
        'The spring term is where exam technique and consistency matter most. Students can focus on weaker topics, practise under timed conditions and use feedback to turn marks into momentum.',
      seoDescription:
        'Spring term GCSE prep for Year 10 and Year 11. Focus on mock-season revision, question practice and AI-marked feedback for the 2027 exams.',
      proof: [
        'Target weak topics now',
        'Exam technique on the rise',
        'Marks turned into next steps',
        'A clearer path to 2027',
      ],
    };
  }

  if (month >= 5 && month <= 6) {
    return {
      badge: 'Summer prep • build for 2027',
      title: 'Summer is the best time to get ahead for 2027.',
      highlight: 'Start the next GCSE cycle early, without burning out.',
      description:
        'This is the perfect window for calm, focused revision. Students can rebuild confidence, cement exam skills and start the next academic year stronger than before.',
      seoDescription:
        'Summer GCSE prep for students aiming at 2027 exams. Strengthen foundations, revise with purpose and keep momentum through the break.',
      proof: [
        'Start 2027 prep early',
        'Stronger daily habits',
        'Clearer revision priorities',
        'Less stress in the autumn',
      ],
    };
  }

  return {
    badge: 'Summer reset • ready for the new year',
    title: 'A strong GCSE 2027 start begins here.',
    highlight: 'Use the summer to build the routine that carries into the year ahead.',
    description:
      'The break is a chance to reset, rebuild confidence and get ahead on the skills that matter most for Year 10 and Year 11 GCSE preparation.',
    seoDescription:
      'Prepare for the new GCSE cycle with Grade9Ready. Build confidence, routines and subject strength for the 2027 exams.',
    proof: [
      'Plan the next year early',
      'Build confidence before term starts',
      'Keep revision active and clear',
      'Focused on 2027 GCSE goals',
    ],
  };
}

const subjects = [
  {
    name: 'Maths',
    slug: 'maths',
    path: '/free-lesson?subject=maths',
    icon: Calculator,
    color: 'bg-indigo-500',
    available: true,
    description:
      'Clear methods, worked examples, adaptive questions and problem-solving practice.',
  },
  {
    name: 'English',
    slug: 'english-language',
    path: '/free-lesson?subject=english',
    icon: BookOpen,
    color: 'bg-rose-500',
    available: true,
    description:
      'Language and literature practice with essay guidance and written-answer feedback.',
  },
  {
    name: 'Science',
    slug: 'combined-science',
    path: '/free-lesson?subject=science',
    icon: Atom,
    color: 'bg-emerald-500',
    available: true,
    description:
      'Biology, Chemistry and Physics lessons with questions and progress tracking.',
  },
  {
    name: 'Geography',
    slug: 'geography',
    path: '/free-lesson?subject=geography',
    icon: Globe,
    color: 'bg-cyan-500',
    available: true,
    description:
      'Physical and human geography, map skills, fieldwork and exam-style practice.',
  },
  {
    name: 'History',
    slug: 'history',
    path: '/signup?interest=history',
    icon: Scroll,
    color: 'bg-orange-500',
    available: false,
    description:
      'Topic knowledge, source analysis and structured written responses are in development.',
  },
  {
    name: 'Spanish',
    slug: 'spanish',
    path: '/signup?interest=spanish',
    icon: Translate,
    color: 'bg-blue-500',
    available: false,
    description:
      'Vocabulary, grammar, translation, reading, listening and speaking are planned.',
  },
];

const revisionLoop = [
  {
    step: '01',
    title: 'Learn',
    icon: BookOpen,
    text: 'Build secure knowledge with concise explanations, worked examples and clear methods.',
  },
  {
    step: '02',
    title: 'Practise',
    icon: Fire,
    text: 'Answer topic questions and exam-style tasks instead of passively rereading notes.',
  },
  {
    step: '03',
    title: 'Improve',
    icon: Brain,
    text: 'Use AI guidance, worked solutions and examiner-style tips to correct mistakes.',
  },
  {
    step: '04',
    title: 'Track',
    icon: ChartLineUp,
    text: 'See completed work, developing strengths and the areas that should come next.',
  },
];

const coreBenefits = [
  {
    icon: Target,
    title: 'Revision with a clear purpose',
    description:
      'Students move from topic knowledge to active practice, rather than collecting notes without testing understanding.',
  },
  {
    icon: Lightbulb,
    title: 'Feedback that points forward',
    description:
      'Written answers receive immediate guidance on strengths, likely improvements and practical next steps.',
  },
  {
    icon: Users,
    title: 'Progress that is easier to see',
    description:
      'Student dashboards show learning activity, while parent features on selected plans provide additional oversight.',
  },
];

const earlyFeedback = [
  {
    quote: 'The feedback helped me understand where I needed to improve.',
    author: 'Year 8 student',
  },
  {
    quote: 'My son’s confidence in Maths has improved.',
    author: 'Parent of a Year 8 student',
  },
  {
    quote: 'Easy to use and fun.',
    author: 'Year 9 student',
  },
];

const faqs = [
  {
    q: 'Who is Grade9Ready for?',
    a: 'Grade9Ready is designed primarily for GCSE students in Years 10 and 11. It can also support younger pupils who are building the knowledge and study habits needed before GCSE courses begin.',
  },
  {
    q: 'Which subjects are available now?',
    a: 'Maths, English, Science and Geography are available now. History and Spanish are in development.',
  },
  {
    q: 'What is included in the 7-day early access trial?',
    a: 'The trial includes a Section 1 sampler across the live subjects, Foundation GCSE Problem Mode, five AI marking credits, progress tracking, badges and end-of-section tests. No payment card is required to start.',
  },
  {
    q: 'How does AI feedback work?',
    a: 'Students submit written answers and receive immediate guidance on what went well, what could be improved and what to focus on next. It supports revision but does not replace teacher or official examiner marking.',
  },
  {
    q: 'Can parents track progress?',
    a: 'Parents manage subscriptions and can link child accounts. More detailed parent readiness dashboards, weak-area reporting and study controls are available on selected paid plans.',
  },
];

function Reveal({ children, className = '', delay = 0 }) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
      whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.4, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function DashboardPreview() {
  return (
    <div
      className="relative"
      role="img"
      aria-label="Illustration of the Grade9Ready revision dashboard"
    >
      <div
        className="absolute -inset-5 rounded-[2rem] bg-gradient-to-br from-primary/25 via-accent/25 to-cyan-400/20 blur-2xl"
        aria-hidden="true"
      />

      <div className="relative neo-card bg-card/95 backdrop-blur p-5 sm:p-6 rotate-[1deg]">
        <div className="flex items-center justify-between gap-4 pb-4 border-b-2 border-foreground/10">
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-muted-foreground">
              Today’s revision focus
            </p>
            <h2 className="font-heading font-black text-xl mt-1">
              Maths: algebra
            </h2>
          </div>

          <div className="w-11 h-11 rounded-xl bg-indigo-500 text-white border-2 border-foreground flex items-center justify-center shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]">
            <Calculator size={23} weight="bold" />
          </div>
        </div>

        <div className="space-y-3 py-5">
          {[
            {
              label: 'Learn: expanding brackets',
              complete: true,
            },
            {
              label: 'Practise: mixed questions',
              complete: true,
            },
            {
              label: 'Improve: correct two errors',
              complete: false,
            },
          ].map((task) => (
            <div
              key={task.label}
              className="flex items-center justify-between gap-3 rounded-xl border-2 border-foreground/10 bg-background p-3"
            >
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle
                  size={19}
                  weight="fill"
                  className={
                    task.complete
                      ? 'text-emerald-500 shrink-0'
                      : 'text-muted-foreground/35 shrink-0'
                  }
                />

                <span className="text-sm font-bold truncate">
                  {task.label}
                </span>
              </div>

              <span className="text-xs font-black text-muted-foreground">
                {task.complete ? 'Done' : 'Next'}
              </span>
            </div>
          ))}
        </div>

        <div className="rounded-xl border-2 border-foreground bg-accent/80 p-4 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]">
          <div className="flex items-center gap-2 mb-2">
            <Brain size={20} weight="bold" />
            <p className="font-heading font-black">Feedback</p>
          </div>

          <p className="text-sm font-bold">
            You chose the correct method.
          </p>

          <p className="text-sm mt-1 opacity-80">
            Next, show every algebraic step so the final answer can be
            checked clearly.
          </p>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-xs font-bold mb-2">
            <span>Weekly target</span>
            <span>4 of 6 activities</span>
          </div>

          <div className="h-3 rounded-full bg-secondary border-2 border-foreground overflow-hidden">
            <div className="h-full w-2/3 bg-primary" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();

  const dashboardPath =
    user?.role === 'parent' ? '/parent/dashboard' : '/dashboard';
  const stage = getSchoolYearStage();

  const trackCta = (cta, location) =>
    trackEvent(EVT.CTA_CLICK, {
      cta,
      location,
    });

  return (
    <div className="min-h-screen">
      <SEO
        title={`${stage.badge} | Grade9Ready`}
        description={stage.seoDescription}
        keywords="GCSE revision UK, Year 10 revision, Year 11 revision, GCSE Maths revision, GCSE English revision, GCSE Science revision, GCSE Geography revision, Grade 9 revision help, UK GCSE prep"
        path="/"
        geo="United Kingdom"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b-2 border-foreground/10">
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.18),transparent_35%),radial-gradient(circle_at_80%_20%,rgba(250,204,21,0.22),transparent_30%)]"
          aria-hidden="true"
        />

        <div
          className="absolute inset-0 opacity-[0.035] bg-[linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)] bg-[size:34px_34px]"
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-28">
          <div className="grid lg:grid-cols-[1.08fr_0.92fr] gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              <div className="inline-flex items-center gap-2 bg-accent border-2 border-foreground rounded-full px-4 py-2 mb-6 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]">
                <Sparkle size={16} weight="fill" />

                <span className="text-xs sm:text-sm font-black uppercase tracking-wide">
                  {stage.badge}
                </span>
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-heading font-black tracking-tight leading-[1.02] mb-6"
                data-testid="home-h1"
              >
                {stage.title}{' '}
                <span className="gradient-text">
                  {stage.highlight}
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed mb-8">
                {stage.description}
              </p>

              <div
                className="flex flex-col sm:flex-row gap-3"
                data-testid="home-cta-row"
              >
                {user ? (
                  <Link
                    to={dashboardPath}
                    onClick={() =>
                      trackCta('go_to_dashboard', 'home_hero')
                    }
                    className="neo-btn flex items-center justify-center gap-2"
                    data-testid="hero-dashboard-btn"
                  >
                    Go to Dashboard
                    <ArrowRight size={20} weight="bold" />
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/signup"
                      onClick={() =>
                        trackCta('start_early_access', 'home_hero')
                      }
                      className="neo-btn flex items-center justify-center gap-2"
                      data-testid="hero-start-early-access-btn"
                    >
                      Create Parent Account
                      <Rocket size={20} weight="bold" />
                    </Link>

                    <Link
                      to="/free-lesson"
                      onClick={() =>
                        trackCta('try_free_lesson', 'home_hero')
                      }
                      className="neo-btn-secondary flex items-center justify-center gap-2"
                      data-testid="hero-try-free-lesson-btn"
                    >
                      Try a Free Lesson
                      <ArrowRight size={18} weight="bold" />
                    </Link>
                  </>
                )}
              </div>

              <ul
                className="grid sm:grid-cols-2 gap-x-5 gap-y-3 mt-8 max-w-2xl"
                data-testid="home-proof-strip"
              >
                {stage.proof.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-sm font-bold"
                  >
                    <CheckCircle
                      size={17}
                      weight="fill"
                      className="text-emerald-500 shrink-0"
                    />

                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <DashboardPreview />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Live platform scale */}
      <LessonCountShowcase />

      {/* Subjects */}
      <section
        className="py-16 md:py-24"
        data-testid="home-subjects-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-primary mb-3">
              Choose where to begin
            </p>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black mb-4">
              GCSE subjects in one focused platform
            </h2>

            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Go straight to the subject and topic that needs attention.
              More subject areas will be added as they are ready.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {subjects.map((subject, index) => (
              <Reveal
                key={subject.slug}
                delay={index * 0.04}
              >
                <Link
                  to={subject.path}
                  onClick={() =>
                    trackCta(
                      subject.available
                        ? `open_${subject.slug}`
                        : `interest_${subject.slug}`,
                      'home_subjects',
                    )
                  }
                  className={`neo-card block h-full group relative overflow-hidden ${
                    subject.available ? '' : 'bg-secondary/35'
                  }`}
                  data-testid={`subject-card-${subject.slug}`}
                >
                  <div
                    className={`absolute top-0 left-0 h-1.5 w-full ${subject.color}`}
                    aria-hidden="true"
                  />

                  <div className="flex items-start gap-4 pt-1">
                    <div
                      className={`w-12 h-12 ${subject.color} text-white rounded-xl border-2 border-foreground flex items-center justify-center shrink-0 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]`}
                    >
                      <subject.icon size={24} weight="bold" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="font-heading font-black text-xl">
                          {subject.name}
                        </h3>

                        {!subject.available && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide bg-background border border-foreground rounded-full px-2 py-1 shrink-0">
                            <Lock size={11} weight="bold" />
                            In development
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-muted-foreground leading-relaxed min-h-[4.5rem]">
                        {subject.description}
                      </p>

                      <div className="flex items-center gap-1 mt-5 text-sm font-black">
                        {subject.available
                          ? 'Try a free lesson'
                          : 'Register your interest'}

                        <ArrowRight
                          size={16}
                          weight="bold"
                          className="group-hover:translate-x-1 transition-transform"
                        />
                      </div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Revision loop */}
      <section className="py-16 md:py-24 bg-secondary/45 border-y-2 border-foreground/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-primary mb-3">
              One clear revision loop
            </p>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black mb-4">
              Learn. Practise. Improve. Track.
            </h2>

            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Every part of Grade9Ready is designed to move students
              from activity to measurable improvement.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {revisionLoop.map((item, index) => (
              <Reveal
                key={item.step}
                delay={index * 0.06}
                className="h-full"
              >
                <div className="neo-card h-full relative">
                  <span className="absolute top-4 right-4 text-4xl font-heading font-black text-foreground/10">
                    {item.step}
                  </span>

                  <div className="w-12 h-12 rounded-xl bg-primary text-white border-2 border-foreground flex items-center justify-center mb-5 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]">
                    <item.icon size={24} weight="bold" />
                  </div>

                  <h3 className="font-heading font-black text-xl mb-2">
                    {item.title}
                  </h3>

                  <p className="text-muted-foreground leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Core benefits */}
      <section
        className="py-16 md:py-24"
        data-testid="home-benefits-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black mb-4">
              More than a library of revision notes
            </h2>

            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Grade9Ready combines knowledge, active practice and
              feedback so students can make better revision decisions.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {coreBenefits.map((benefit, index) => (
              <Reveal
                key={benefit.title}
                delay={index * 0.06}
                className="h-full"
              >
                <div className="neo-card h-full">
                  <div className="w-12 h-12 bg-accent rounded-xl border-2 border-foreground flex items-center justify-center mb-5 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]">
                    <benefit.icon size={24} weight="bold" />
                  </div>

                  <h3 className="font-heading font-black text-xl mb-3">
                    {benefit.title}
                  </h3>

                  <p className="text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Student and parent routes */}
      <section className="py-16 md:py-24 bg-primary text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <Reveal className="h-full">
              <div className="h-full rounded-2xl border-2 border-white/30 bg-white/10 backdrop-blur p-7 md:p-9">
                <div className="w-12 h-12 rounded-xl bg-white text-primary border-2 border-white flex items-center justify-center mb-5">
                  <GraduationCap size={25} weight="fill" />
                </div>

                <p className="text-xs font-black uppercase tracking-[0.2em] text-white/65 mb-2">
                  For students
                </p>

                <h2 className="font-heading font-black text-3xl mb-4">
                  Turn revision time into useful evidence
                </h2>

                <p className="text-white/80 leading-relaxed mb-6">
                  Work through a topic, test your understanding and use
                  feedback to decide what to repeat, correct or move on
                  from.
                </p>

                <ul className="space-y-3 mb-7">
                  {[
                    'Clear topic-by-topic learning',
                    'Foundation and Higher practice on paid plans',
                    'Worked solutions and improvement guidance',
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-2 font-bold"
                    >
                      <CheckCircle
                        size={19}
                        weight="fill"
                        className="text-accent shrink-0"
                      />

                      {item}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/free-lesson"
                  onClick={() =>
                    trackCta(
                      'try_free_lesson',
                      'home_student_route',
                    )
                  }
                  className="inline-flex items-center gap-2 bg-white text-primary font-black px-5 py-3 rounded-xl border-2 border-white shadow-[3px_3px_0px_0px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 transition-transform"
                >
                  Try a Free Lesson
                  <ArrowRight size={18} weight="bold" />
                </Link>
              </div>
            </Reveal>

            <Reveal
              className="h-full"
              delay={0.08}
            >
              <div className="h-full rounded-2xl border-2 border-white/30 bg-white/10 backdrop-blur p-7 md:p-9">
                <div className="w-12 h-12 rounded-xl bg-accent text-foreground border-2 border-white flex items-center justify-center mb-5">
                  <Users size={25} weight="fill" />
                </div>

                <p className="text-xs font-black uppercase tracking-[0.2em] text-white/65 mb-2">
                  For parents
                </p>

                <h2 className="font-heading font-black text-3xl mb-4">
                  Support revision without having to guess
                </h2>

                <p className="text-white/80 leading-relaxed mb-6">
                  Parent accounts manage subscriptions and linked student
                  accounts. Selected plans add deeper progress, weak-area
                  and study-planning features.
                </p>

                <ul className="space-y-3 mb-7">
                  {[
                    'Separate parent and student logins',
                    'Progress visibility across linked accounts',
                    'More detailed readiness tools on selected plans',
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-2 font-bold"
                    >
                      <CheckCircle
                        size={19}
                        weight="fill"
                        className="text-accent shrink-0"
                      />

                      {item}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/pricing"
                  onClick={() =>
                    trackCta(
                      'view_parent_plans',
                      'home_parent_route',
                    )
                  }
                  className="inline-flex items-center gap-2 bg-accent text-foreground font-black px-5 py-3 rounded-xl border-2 border-white shadow-[3px_3px_0px_0px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 transition-transform"
                >
                  Compare Plans
                  <ArrowRight size={18} weight="bold" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Free guide */}
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link
              to="/get-ready"
              onClick={() =>
                trackCta(
                  'open_get_ready_guide',
                  'home_get_ready',
                )
              }
              className="neo-card block bg-accent group hover:-translate-y-1 transition-transform"
              data-testid="get-ready-card"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-5">
                <div className="w-14 h-14 rounded-xl bg-foreground text-background flex items-center justify-center shrink-0">
                  <GraduationCap size={29} weight="fill" />
                </div>

                <div className="flex-1">
                  <p className="text-xs font-black uppercase tracking-[0.2em] opacity-65 mb-1">
                    Free interactive guide
                  </p>

                  <h2 className="font-heading font-black text-2xl mb-1">
                    How to Get Ready for Grade 9
                  </h2>

                  <p className="opacity-80">
                    Explore the study habits, revision methods and
                    mindset that make consistent progress more likely.
                  </p>
                </div>

                <div className="flex items-center gap-1 font-black shrink-0">
                  Open guide

                  <ArrowRight
                    size={18}
                    weight="bold"
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </div>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Early tester feedback */}
      <section className="py-16 md:py-24 bg-secondary/45 border-y-2 border-foreground/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-background border-2 border-foreground rounded-full px-4 py-2 mb-4">
              <Star
                size={16}
                weight="fill"
                className="text-accent"
              />

              <span className="text-xs font-black uppercase tracking-wide">
                Feedback from early testers
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-heading font-black">
              Early reactions to Grade9Ready
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {earlyFeedback.map((item, index) => (
              <Reveal
                key={item.quote}
                delay={index * 0.06}
                className="h-full"
              >
                <figure className="neo-card h-full flex flex-col">
                  <blockquote className="text-lg font-bold leading-relaxed flex-1">
                    “{item.quote}”
                  </blockquote>

                  <figcaption className="text-sm text-muted-foreground mt-5">
                    {item.author}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Early access CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="neo-card relative overflow-hidden text-center p-7 sm:p-10 md:p-12">
              <div
                className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-indigo-500 via-accent to-emerald-500"
                aria-hidden="true"
              />

              <div className="w-14 h-14 rounded-2xl bg-primary text-white border-2 border-foreground flex items-center justify-center mx-auto mb-5 shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
                <Rocket size={28} weight="fill" />
              </div>

              <h2 className="text-3xl sm:text-4xl font-heading font-black mb-4">
                Explore Grade9Ready free for 7 days
              </h2>

              <p className="text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed mb-6">
                Try selected lessons across every live subject,
                Foundation Problem Mode, five AI marking credits,
                progress tracking and end-of-section tests. No card is
                required.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {user ? (
                  <Link
                    to={dashboardPath}
                    onClick={() =>
                      trackCta(
                        'go_to_dashboard',
                        'home_final_cta',
                      )
                    }
                    className="neo-btn inline-flex items-center justify-center gap-2"
                  >
                    Go to Dashboard
                    <ArrowRight size={18} weight="bold" />
                  </Link>
                ) : (
                  <Link
                    to="/signup"
                    onClick={() =>
                      trackCta(
                        'start_early_access',
                        'home_final_cta',
                      )
                    }
                    className="neo-btn inline-flex items-center justify-center gap-2"
                  >
                    Create Parent Account
                    <Rocket size={18} weight="bold" />
                  </Link>
                )}

                <Link
                  to="/pricing"
                  onClick={() =>
                    trackCta('view_plans', 'home_final_cta')
                  }
                  className="neo-btn-secondary inline-flex items-center justify-center gap-2"
                >
                  Compare Plans
                  <ArrowRight size={18} weight="bold" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-heading font-black mb-3">
              Frequently asked questions
            </h2>

            <p className="text-muted-foreground">
              The essentials before creating a Grade9Ready account.
            </p>
          </Reveal>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Reveal
                key={faq.q}
                delay={index * 0.03}
              >
                <details className="neo-card group">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-heading font-black text-lg">
                    <span>{faq.q}</span>

                    <span
                      className="w-8 h-8 rounded-full border-2 border-foreground flex items-center justify-center text-xl leading-none group-open:rotate-45 transition-transform shrink-0"
                      aria-hidden="true"
                    >
                      +
                    </span>
                  </summary>

                  <p className="text-muted-foreground leading-relaxed mt-4 pr-10">
                    {faq.a}
                  </p>
                </details>
              </Reveal>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/faq"
              onClick={() =>
                trackCta('view_all_faqs', 'home_faq')
              }
              className="neo-btn-secondary"
            >
              View All FAQs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}